import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogTrigger,
  EmptyState,
  LoadingOrbit,
  VideoPlayerWithTimeline,
  useToast,
} from '@chatsdet/ui';
import {
  DownloadSimple,
  FlowArrow,
  LinkSimple,
  MagnifyingGlass,
  Play,
} from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useExternalScenarioDetail } from '../../features/external-tests/useExternalScenarioDetail';
import { groupScenarios } from '../../features/external-tests/scenarios';
import {
  useExternalRunDiagnostics,
  useExternalRunNavigation,
} from '../../features/external-tests/useExternalRunDiagnostics';
import { useExternalFailureDiagnostic } from '../../features/external-tests/useExternalFailureDiagnostic';
import { useExternalTestConversion } from '../../features/external-tests/useExternalTestConversion';
import { resolveShownDiagnosis } from './ExternalTestItemDetail/resolveShownDiagnosis';
import { ApiError } from '../../lib/api-error';
import type { VideoTimeline } from '@chatsdet/types';
import { resolveVideoSrc } from '../utils/resolveVideoSrc';
import { AgentPromptButton } from '../components/agent-prompts/AgentPromptButton';
import { API_URL } from '../../config';
import { RunDetailHeader } from './RunDetailPage/RunDetailHeader';
import { RunTimelineInspector } from './RunDetailPage/RunTimelineInspector';
import { ExternalTestMain } from './ExternalTestItemDetail/ExternalTestMain';
import { ExternalTestRail } from './ExternalTestItemDetail/ExternalTestRail';
import { ExternalStepProgress } from './ExternalTestItemDetail/ExternalStepProgress';
import { stepOutcomeOf, type StepOutcome } from './ExternalTestItemDetail/stepModel';
import { ExternalRecordingPlaceholder } from './ExternalTestItemDetail/ExternalRecordingPlaceholder';
import { FailureDiagnosticSection } from './FlowDesigner/runs/FailureDiagnosticSection';
import { rootCauseSeverityHex } from '../components/issues/diagnosticDisplay';

const VERDICT_LABEL = {
  passed: 'Passed',
  failed: 'Failed',
  flaky: 'Flaky',
  skipped: 'Skipped',
} as const;

// DialogTrigger requires a trigger element; these dialogs are opened by state, so
// the hidden span only satisfies that API (same workaround as the other pages).
function ControlledDialog({
  isOpen,
  onOpenChange,
  title,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="4xl">
      <span style={{ display: 'none' }} />
      <Dialog title={title}>{children}</Dialog>
    </DialogTrigger>
  );
}

// Conversion reads Selenium source from a linked repository. Playwright has
// capture support but no repository-backed conversion endpoint, so keep this
// capability explicit rather than coupling the detail page to a raw provider
// branch that could accidentally expose an unsupported action.
const supportsFlowConversion = (provider: string) => provider === 'selenium';

/**
 * One external test (a ticketed scenario like WEB-31415): the shared run detail
 * header and a step progress bar over two scrolling columns — the steps (with
 * the failing step expanded) in the centre, and the evidence (failure analysis,
 * artifacts, run details, history) in a right-hand column.
 *
 * The shared `RunDetailHeader` / `RunTimelineInspector` are reused rather than
 * reimplemented, so the two pages cannot drift apart — an external run should
 * feel like one of ours, since to the customer it is just another failing test.
 */
export function ExternalTestItemDetail() {
  const { runId, itemId } = useParams({ from: '/runs/external/$runId/item/$itemId' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: run, isLoading, isError } = useExternalScenarioDetail(runId, itemId);
  const [diagnosticsRequested, setDiagnosticsRequested] = React.useState(false);
  const [selectedAttemptId, setSelectedAttemptId] = React.useState<string | null>(itemId);
  const [currentTimeMs, setCurrentTimeMs] = React.useState(0);
  const [seekRequest, setSeekRequest] = React.useState<{ timeMs: number; version: number } | null>(
    null
  );
  const [recordingOpen, setRecordingOpen] = React.useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = React.useState(false);
  const [analysisOpen, setAnalysisOpen] = React.useState(false);

  // The URL addresses a step id, but the page shows the TEST that step belongs
  // to — so a link to any step of WEB-31415 opens WEB-31415.
  const scenarios = React.useMemo(
    () => (run ? groupScenarios(run.items, run.provider) : []),
    [run]
  );
  const routeItem = run?.items.find((item) => item.id === itemId) ?? null;
  const scenario = React.useMemo(
    () =>
      scenarios.find((candidate) =>
        candidate.steps.some((step) => step.testKey === routeItem?.testKey || step.id === itemId)
      ) ?? null,
    [scenarios, routeItem?.testKey, itemId]
  );
  const focusedTestKey = routeItem?.testKey ?? scenario?.steps[0]?.testKey ?? null;
  const isPlaywrightScenario = run?.provider === 'playwright';
  const attempts = React.useMemo(
    () =>
      run?.items
        .filter((item) => item.testKey === focusedTestKey)
        .sort((a, b) => b.attempt - a.attempt) ?? [],
    [run?.items, focusedTestKey]
  );
  const selectedItem =
    attempts.find((attempt) => attempt.id === selectedAttemptId) ??
    attempts.find((attempt) => attempt.id === itemId) ??
    attempts[0] ??
    null;
  const conversionItemId = isPlaywrightScenario ? (selectedItem?.id ?? itemId) : itemId;
  const conversion = useExternalTestConversion(runId, conversionItemId);
  const selectedScenario = React.useMemo(() => {
    if (!run || !scenario || !selectedItem || !focusedTestKey || !isPlaywrightScenario)
      return scenario;
    return (
      groupScenarios(
        [...run.items.filter((item) => item.testKey !== focusedTestKey), selectedItem],
        run.provider
      ).find((candidate) => candidate.key === scenario.key) ?? scenario
    );
  }, [run, scenario, selectedItem, focusedTestKey, isPlaywrightScenario]);
  const diagnosticTarget = isPlaywrightScenario
    ? selectedItem?.status === 'failed'
      ? selectedItem
      : null
    : (scenario?.steps.find((step) => step.verdict === 'failed') ?? null);
  const diagnosticEnabled = isPlaywrightScenario
    ? selectedItem?.status === 'failed'
    : scenario?.verdict === 'failed';

  // Land on the failure, not the start of a recording of which one slice
  // matters. Falls back to the first captured step.
  const anchor = isPlaywrightScenario
    ? selectedItem?.capture
      ? selectedItem
      : null
    : scenario?.firstFailure?.capture
      ? scenario.firstFailure
      : (scenario?.steps.find((step) => step.capture) ?? null);

  const videoSrc = resolveVideoSrc(anchor?.capture?.videoStorageKey ?? null);
  const downloadSrc = videoSrc
    ? `${videoSrc}${videoSrc.includes('?') ? '&' : '?'}download=1`
    : null;
  const anchorOffsetMs = anchor?.capture?.videoOffsetMs ?? null;

  // Diagnostics are sliced server-side to the anchor step's window and rebased
  // onto the video clock, so the panels need no arithmetic here.
  const diagnostics = useExternalRunDiagnostics(runId, anchor?.id ?? null, diagnosticsRequested);
  const navigation = useExternalRunNavigation(runId, anchor?.id ?? null, Boolean(videoSrc));
  const failureDiagnostic = useExternalFailureDiagnostic(
    runId,
    diagnosticTarget?.id ?? null,
    diagnosticEnabled
  );

  // A repeat failure inherits a thin diagnosis; fetch the original so its full
  // analysis can be shown. A failed fetch (including a 404) sets the query error,
  // so status comes out null and the carried diagnosis is shown as before.
  const repeatOf = failureDiagnostic.diagnostic?.triage?.repeatOf;
  const {
    id: originalId,
    status: originalStatus,
    diagnostic: originalDiagnostic,
  } = useExternalFailureDiagnostic(
    repeatOf?.runId ?? '',
    repeatOf?.itemId ?? null,
    Boolean(repeatOf)
  );
  const shownDiagnostic = React.useMemo(
    () =>
      resolveShownDiagnosis(failureDiagnostic.diagnostic, {
        id: originalId,
        status: originalStatus,
        diagnostic: originalDiagnostic,
      }),
    [failureDiagnostic.diagnostic, originalId, originalStatus, originalDiagnostic]
  );

  // Open on the failure rather than 0:00.
  React.useEffect(() => {
    if (anchorOffsetMs === null) return;
    setSeekRequest((prev) => ({ timeMs: anchorOffsetMs, version: (prev?.version ?? 0) + 1 }));
  }, [anchor?.id, anchorOffsetMs]);

  // The recording lives in a dialog now, so any request to look at a moment
  // (a step, a diagnostic event, a console line) also brings the player up.
  // Seeking from inside the diagnostics dialog deliberately closes it and opens the player.
  const handleSeek = React.useCallback((timeMs: number) => {
    setSeekRequest((prev) => ({ timeMs, version: (prev?.version ?? 0) + 1 }));
    setDiagnosticsOpen(false);
    setRecordingOpen(true);
  }, []);

  const handleSelectAttempt = React.useCallback((attemptId: string) => {
    setSelectedAttemptId(attemptId);
    setCurrentTimeMs(0);
    setDiagnosticsRequested(false);
    setSeekRequest((prev) => ({ timeMs: 0, version: (prev?.version ?? 0) + 1 }));
  }, []);

  const handleViewAnalysis = React.useCallback(() => setAnalysisOpen(true), []);

  const handleOpenDiagnostics = React.useCallback(() => {
    setDiagnosticsRequested(true);
    setDiagnosticsOpen(true);
  }, []);

  const handleCopyLink = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  }, [toast]);

  // One entry per captured step, so the scrubber is a map of the test. The
  // player only renders its side panel when a timeline exists, so this is
  // load-bearing rather than decoration.
  const timeline = React.useMemo(() => {
    if (!selectedScenario) return null;
    const entries = selectedScenario.steps
      .filter((step) => step.capture?.videoOffsetMs != null)
      .map((step) => ({
        action: step.verdict === 'failed' ? 'failed' : 'test',
        label: step.stepLabel,
        success: step.verdict === 'passed',
        error: step.verdict === 'failed' ? (step.error?.message ?? 'Failed') : undefined,
        timestampMs: step.capture!.videoOffsetMs as number,
        isoTimestamp: new Date(step.capture!.videoOffsetMs as number).toISOString(),
      }));
    if (!entries.length) return null;
    return {
      startTimestamp: new Date(0).toISOString(),
      entries,
      navigationEvents: navigation.data?.navigationEvents.map(({ url, timestampMs }) => ({
        url,
        timestampMs,
      })),
    };
  }, [navigation.data?.navigationEvents, selectedScenario]);

  const diagnosticTimeline = React.useMemo<VideoTimeline | null>(() => {
    const events = shownDiagnostic?.rootCauseTimeline;
    if (!events?.length) return null;
    return {
      startTimestamp: timeline?.startTimestamp ?? new Date(0).toISOString(),
      entries: events.map((event) => ({
        action: 'annotation',
        target: event.title,
        success: true,
        timestampMs: Math.max(0, event.timestampMs),
        isoTimestamp: new Date(Math.max(0, event.timestampMs)).toISOString(),
        isAnnotation: true,
        annotationType: 'diagnostic-event',
        annotationColor: rootCauseSeverityHex(event.severity),
      })),
    };
  }, [shownDiagnostic, timeline?.startTimestamp]);

  const convertedWorkflow = isPlaywrightScenario
    ? selectedItem?.convertedWorkflow
    : scenario?.steps.find((step) => step.convertedWorkflow)?.convertedWorkflow;

  const openConvertedFlow = React.useCallback(async () => {
    if (convertedWorkflow && convertedWorkflow.state !== 'failed') {
      await navigate({
        to: '/flows/$workflowId',
        params: { workflowId: convertedWorkflow.workflowId },
      });
      return;
    }
    try {
      const result = await conversion.mutateAsync();
      toast.success(result.created ? 'Converting to a native workflow' : 'Retrying conversion');
      await navigate({ to: '/flows/$workflowId', params: { workflowId: result.workflowId } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to convert external test', {
        reportError: !(error instanceof ApiError && error.userError),
      });
    }
  }, [conversion, convertedWorkflow, navigate, toast]);

  useBreadcrumbs([
    { label: 'Runs', href: '/runs/test' },
    { label: run?.runnerName ?? 'External run', href: `/runs/external/${runId}` },
    ...(run?.area
      ? [
          {
            label: run.area.label,
            href: `/runs/external/${runId}?area=${run.area.index}&view=grouped`,
          },
        ]
      : []),
    { label: scenario?.label ?? 'Test' },
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (isError || !run || !scenario || !selectedItem || !selectedScenario) {
    return (
      <EmptyState
        icon={<Play size={48} />}
        title="Test not found"
        description="This test result could not be loaded."
      />
    );
  }

  const visibleScenario = isPlaywrightScenario ? selectedScenario : scenario;

  const stepOutcomes: StepOutcome[] = isPlaywrightScenario
    ? (selectedItem.nativeSteps ?? []).map((step) => step.status)
    : selectedScenario.steps.map((step) => stepOutcomeOf(step.verdict));

  const diagnosticInProgress =
    failureDiagnostic.status === 'pending' || failureDiagnostic.status === 'running';
  const diagnosticCompleted = failureDiagnostic.status === 'completed';
  const diagnoseLabel = diagnosticCompleted
    ? 'Diagnosed'
    : diagnosticInProgress
      ? 'Diagnosing…'
      : failureDiagnostic.status === 'failed'
        ? 'Retry diagnosis'
        : 'Diagnose';
  return (
    // Bound to the viewport below the 52px app bar so the header stays fixed and
    // each column owns its own scroll — the same lockup as the run detail page.
    <div className="flex h-[calc(100dvh-52px)] flex-col overflow-hidden">
      <RunDetailHeader
        title={visibleScenario.label}
        status={visibleScenario.verdict === 'flaky' ? 'flaky_success' : visibleScenario.verdict}
        statusLabel={VERDICT_LABEL[visibleScenario.verdict]}
        startedAt={selectedItem.startedAt}
        finishedAt={selectedItem.finishedAt}
        isRunning={
          !selectedItem.finishedAt && (run.status === 'open' || run.status === 'collecting')
        }
        tags={selectedItem.tags}
        actions={
          <div className="flex items-center gap-2">
            <AgentPromptButton
              promptUrl={`${API_URL}/external-tests/runs/${runId}/items/${itemId}/agent-prompt`}
              label="Copy prompt"
              tooltip="Copies a prompt that lets a coding agent diagnose this failure with the Cofactor CLI"
            />
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Copy link to this test"
              aria-label="Copy link to this test"
              onPress={handleCopyLink}
            >
              <LinkSimple size={16} aria-hidden="true" />
            </Button>
            {downloadSrc ? (
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Download test recording"
                aria-label="Download test recording"
                onPress={() => window.open(downloadSrc, '_blank', 'noopener,noreferrer')}
              >
                <DownloadSimple size={16} aria-hidden="true" />
              </Button>
            ) : null}
            {diagnosticEnabled ? (
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip={diagnoseLabel}
                aria-label={diagnoseLabel}
                onPress={failureDiagnostic.triggerDiagnostic}
                isDisabled={
                  failureDiagnostic.isLoading ||
                  failureDiagnostic.isTriggering ||
                  diagnosticInProgress ||
                  diagnosticCompleted
                }
              >
                {diagnosticInProgress || failureDiagnostic.isTriggering ? (
                  <LoadingOrbit size="sm" />
                ) : (
                  <MagnifyingGlass size={16} aria-hidden="true" />
                )}
              </Button>
            ) : null}
            {supportsFlowConversion(run.provider) ? (
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip={
                  convertedWorkflow?.state === 'ready'
                    ? 'View converted flow'
                    : convertedWorkflow?.state === 'generating'
                      ? 'Conversion in progress'
                      : convertedWorkflow?.state === 'failed'
                        ? 'Retry conversion'
                        : 'Convert to flow'
                }
                aria-label={
                  convertedWorkflow?.state === 'ready'
                    ? 'View converted flow'
                    : convertedWorkflow?.state === 'generating'
                      ? 'Conversion in progress'
                      : convertedWorkflow?.state === 'failed'
                        ? 'Retry conversion'
                        : 'Convert to flow'
                }
                onPress={openConvertedFlow}
                isDisabled={conversion.isPending}
              >
                {conversion.isPending || convertedWorkflow?.state === 'generating' ? (
                  <LoadingOrbit size="sm" />
                ) : (
                  <FlowArrow size={16} />
                )}
              </Button>
            ) : null}
          </div>
        }
      />

      <ExternalStepProgress outcomes={stepOutcomes} />

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto bg-background p-4">
          <ExternalTestMain
            scenario={selectedScenario}
            run={run}
            selectedItem={selectedItem}
            attempts={attempts}
            recordingSource={
              anchor && anchor.id !== selectedItem.id
                ? `Attempt ${anchor.attempt} · ${scenario.steps.find((step) => step.id === anchor.id)?.stepLabel ?? anchor.displayName}`
                : null
            }
            onSelectAttempt={handleSelectAttempt}
            onSeek={videoSrc ? handleSeek : undefined}
          />
        </div>

        <div className="w-[clamp(300px,28vw,400px)] shrink-0 overflow-y-auto border-l border-border bg-background p-4">
          <ExternalTestRail
            run={run}
            runId={runId}
            selectedItem={selectedItem}
            diagnosticEnabled={Boolean(diagnosticEnabled)}
            diagnosticStatus={failureDiagnostic.status}
            diagnostic={shownDiagnostic}
            onTriggerDiagnostic={failureDiagnostic.triggerDiagnostic}
            isTriggeringDiagnostic={failureDiagnostic.isTriggering}
            onSeek={videoSrc ? handleSeek : undefined}
            onViewAnalysis={handleViewAnalysis}
            hasRecording={Boolean(videoSrc)}
            onOpenRecording={() => setRecordingOpen(true)}
            onOpenDiagnostics={handleOpenDiagnostics}
            diagnosticCounts={
              diagnostics.data
                ? {
                    console: diagnostics.data.consoleLogs.length,
                    network: diagnostics.data.networkRequests.length,
                  }
                : null
            }
          />
        </div>
      </div>

      <ControlledDialog
        isOpen={recordingOpen}
        onOpenChange={setRecordingOpen}
        title="Recording"
      >
          {videoSrc ? (
            <VideoPlayerWithTimeline
              posterSrc={null}
              videoSrc={videoSrc}
              timeline={timeline}
              diagnosticTimeline={diagnosticTimeline}
              alt={`Recording of ${visibleScenario.label}`}
              showThoughts={false}
              showChapters
              seekRequest={seekRequest}
              onTimeUpdate={setCurrentTimeMs}
              flush
              showFullscreen={false}
              className="w-full"
            />
          ) : (
            <ExternalRecordingPlaceholder
              provider={run.provider}
              runStatus={run.status}
              verdict={visibleScenario.verdict}
              recordingStatus={run.recordings?.status ?? 'not_requested'}
            />
          )}
      </ControlledDialog>

      <ControlledDialog
        isOpen={diagnosticsOpen}
        onOpenChange={setDiagnosticsOpen}
        title="Network and console"
      >
          <RunTimelineInspector
            consoleLogs={diagnostics.data?.consoleLogs}
            networkRequests={diagnostics.data?.networkRequests}
            videoStartedAt={diagnostics.data?.videoStartedAt}
            diagnosticsLoading={diagnostics.isLoading && diagnosticsRequested}
            onLoadDiagnostics={() => setDiagnosticsRequested(true)}
            currentTimeMs={currentTimeMs}
            onSeek={handleSeek}
            className="h-[420px] border border-border"
          />
      </ControlledDialog>

      <ControlledDialog
        isOpen={analysisOpen}
        onOpenChange={setAnalysisOpen}
        title="Failure analysis"
      >
          <FailureDiagnosticSection
            status={failureDiagnostic.status}
            diagnostic={shownDiagnostic}
            onTrigger={failureDiagnostic.triggerDiagnostic}
            isTriggering={failureDiagnostic.isTriggering}
          />
      </ControlledDialog>
    </div>
  );
}
